// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonPresentationProps } from "@insite/mobius/Button";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { fontConfig } from "@steiner/XDL/PreStyle/typography";
import { xBaseTheme } from "@steiner/XDL/types";
import { color } from "@steiner/XDL/Utilities";
import { css } from "styled-components";

export const buttonTransparent = css`
    &,
    &:focus:not([disabled]),
    &:active:not([disabled]),
    &:disabled,
    &:focus:hover,
    &:hover:not(:disabled) {
        border: none;
        background-color: transparent;
        box-shadow: none;
        outline: 0;
        transform: translate(0, 0);
        min-height: initial;
    }
`;

const buttonColors: { [key: string]: { main: string; hover: string; text: string; focus: string } } = {
    primary: {
        main: "primary.main",
        hover: "common.ui9",
        text: "common.white",
        focus: "common.ui10",
    },
    secondary: {
        main: "primary.main",
        hover: "common.ui9",
        text: "primary.main",
        focus: "common.ui10",
    },
    tertiary: {
        main: "common.ui4",
        hover: "common.ui3",
        text: "common.ui4",
        focus: "secondary",
    },
    addToCart: {
        main: "secondary.main",
        hover: "common.ui12",
        text: "text.main",
        focus: "common.ui13",
    },
    link: {
        main: "text.link",
        hover: "common.ui9",
        text: "text.link",
        focus: "common.ui9",
    },
};

export const addToCartButtonStyles: ButtonPresentationProps = {
    ...ButtonPresentationPropsDefault,
    mergeCss: true,
    color: buttonColors.addToCart.main,
    buttonType: "solid",
    typographyProps: {
        color: buttonColors.addToCart.text,
        weight: fontConfig.weight.semiBold,
        css: css`
            letter-spacing: 0;
            text-align: center;
        `,
    },
    css: css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 1px;
        padding: 0 12px;

        &:not(:disabled):hover {
            background-color: ${color(buttonColors.addToCart.hover)};
            border-color: ${color(buttonColors.addToCart.hover)};
        }
        &:not(:disabled):focus {
            outline: none;
            background-color: ${color(buttonColors.addToCart.focus)};
            border-color: ${color(buttonColors.addToCart.focus)};
        }
        &:disabled {
            background-color: ${color(buttonColors.addToCart.main)};
            border-color: ${color(buttonColors.addToCart.main)};
            opacity: 0.33;
        }
    `,
};

export default {
    primary: {
        ...ButtonPresentationPropsDefault,
        mergeCss: true,
        color: buttonColors.primary.main,
        buttonType: "solid",
        typographyProps: {
            color: buttonColors.primary.text,
            weight: fontConfig.weight.semiBold,
            css: css`
                letter-spacing: 0;
                text-align: center;
            `,
        },
        css: css`
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 1px;

            &:not(:disabled):hover {
                background-color: ${color(buttonColors.primary.hover)};
                border-color: ${color(buttonColors.primary.hover)};
            }
            &:not(:disabled):focus {
                outline: none;
                background-color: ${color(buttonColors.primary.focus)};
                border-color: ${color(buttonColors.primary.focus)};
            }
            &:disabled {
                background-color: ${color(buttonColors.primary.main)};
                border-color: ${color(buttonColors.primary.main)};
                opacity: 0.33;
            }
        `,
    },
    secondary: {
        ...ButtonPresentationPropsDefault,
        color: "transparent",
        buttonType: "outline",
        mergeCss: true,
        typographyProps: {
            color: buttonColors.secondary.text,
            weight: fontConfig.weight.semiBold,
            css: css`
                letter-spacing: 0;
                text-align: center;
            `,
        },
        css: css`
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: 1px solid ${color(buttonColors.secondary.main)};
            border-radius: 2px;

            &:not(:disabled):hover {
                border-color: ${color(buttonColors.secondary.hover)};
                & > ${TypographyStyle} {
                    color: ${color(buttonColors.secondary.hover)};
                }
            }
            &:not(:disabled):focus {
                outline: none;
                border-color: ${color(buttonColors.secondary.focus)};
                & > ${TypographyStyle} {
                    color: ${color(buttonColors.secondary.focus)};
                }
            }
            &:disabled {
                border-color: ${color(buttonColors.secondary.main)};
                opacity: 0.33;
            }
        `,
    },
    tertiary: {
        ...ButtonPresentationPropsDefault,
        color: "transparent",
        buttonType: "outline",
        mergeCss: true,
        typographyProps: {
            weight: fontConfig.weight.semiBold,
            css: css`
                letter-spacing: 0;
                text-align: center;
            `,
        },
        css: css`
            display: inline-flex;
            align-items: center;
            justify-content: center;

            color: ${color("common.ui8")};
            border: 1px solid ${color("common.ui8")};
            border-radius: 2px;

            &:not(:disabled):hover {
                color: ${color("common.ui9")};
                border-color: ${color("common.ui9")};
            }
            &:not(:disabled):focus {
                outline: none;
                color: ${color("common.ui10")};
                border-color: ${color("common.ui10")};
            }
            &:disabled {
                color: ${color("common.ui8")};
                border-color: ${color("common.ui8")};
                opacity: 0.33;
            }
        `,
    },
    addToCart: {
        ...addToCartButtonStyles,
    },
    link: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.link.main,
        buttonType: "outline",
        mergeCss: true,
        css: css`
            ${buttonTransparent}
            padding: 0;
            &:disabled {
                opacity: 0.33;
                cursor: not-allowed;
                color: ${color(buttonColors.link.text)};
            }
            &:hover:not(:disabled) {
                ${TypographyStyle},${IconWrapper}, svg {
                    color: ${color(buttonColors.link.hover)};
                }
            }
        `,
    },
} as xBaseTheme["button"];
